import * as React from 'react';
import {
  Operation,
  multiOperations,
  singleOperations,
  NiCommonCascader,
  SELECTION_KEY,
  SELECTION_VALUE_TYPE,
  SelectionOption,
  SpecialValueType
} from '@config/index';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { cloneDeep, debounce } from 'lodash';
import {
  Attribute,
  TriggerCondition,
  ConditionContent,
  ParameterConfig,
  ReferenceNode,
  ParameterType,
  ResponseConfig,
  InputParameter,
  FrameType
} from '@interface/index';
import {
  NiTable,
  discardModal,
  NiForm,
  InputParameterComp,
  AddButton
} from '@component/index';
import { NiModal } from '@component/ant-component/modal';
import { OperationComp } from './operation';
import { ValueSwitch } from './value-switch';
import { FormattedMessage } from 'react-intl';
import { localizedString } from '@locale/index';
import { ParameterMapStore, isEqual, isSystemBoolean } from '@lib/index';

import '@style/component/trigger-condition.less';

export const OperationColumnComp = ({
  disabled,
  index,
  data,
  isBoolean,
  isTable,
  isMultiValue,
  record,
  handleOperationChange
}: {
  disabled?: boolean;
  index: number;
  data: TriggerCondition;
  isBoolean: boolean;
  isTable: boolean;
  isMultiValue: boolean;
  record: ConditionContent;
  handleOperationChange: (next: TriggerCondition) => void;
}) => {

  const node = data.variable && data.variable.length > 0 && data.variable[data.variable.length - 1];
  const nodeSlot: any = node && node.id && ParameterMapStore.get(node.id);
  const isFrameType = nodeSlot.proto && nodeSlot.proto.type === ParameterType.COMPOSITE || false;

  return (
    <OperationComp
      disabled={disabled}
      disabledOperations={[
        ...(isBoolean
          ? [...multiOperations, ...singleOperations]
            .filter(
              (operation: Operation) =>
                operation !== Operation.EqualToCondition
            )
          : []),
        ...(isTable ? [] : data.conditions.map((c: ConditionContent) => c.operation))
      ]}
      data={record.operation}
      isTable={isTable}
      isMultiValue={isMultiValue}
      isFrameType={isFrameType}
      onChange={(next: Operation) => {
        const nextData = cloneDeep(data);

        nextData.conditions[index].operation = next;
        nextData.conditions[index].value = [];

        handleOperationChange(nextData);
      }}
    />
  );
};

export interface TriggerTablePropsConfig {
  response?: ResponseConfig;
  disabled?: boolean;
  triggerConditions: TriggerCondition[];
  frameId: string;
  frameType: string;
  visible: boolean;
  data?: TriggerCondition;
  confirm: (next: TriggerCondition) => void;
  close: () => void;
  parameters: ParameterConfig[];
}
export interface TriggerTableStateConfig {
  data?: TriggerCondition;
  isTable: boolean;
  isBoolean: boolean;
  isMultiValue: boolean;
  isFrameSlot: boolean;
  disabledValues?: string[][];
}

export class TriggerTable extends React.Component<
  TriggerTablePropsConfig,
  TriggerTableStateConfig
> {
  public state: TriggerTableStateConfig = {
    data: undefined,
    isTable: false,
    isBoolean: false,
    isMultiValue: false,
    isFrameSlot: false
  };

  componentDidMount() {
    this.onInit();
  }

  componentDidUpdate(prevProps: any) {
    if (!isEqual(prevProps.visible, this.props.visible) && this.props.visible) {
      this.onInit();
    }
  }

  render() {
    const props = this.props;
    const state = this.state;

    return (
      <NiModal
        open={props.visible}
        destroyOnClose={true}
        closable={false}
        // tslint:disable-next-line
        okText={localizedString('common_save')}
        title={localizedString('condition')}
        onCancel={this.onCancel}
        onOk={this.onConfirm}
        okButtonProps={props.disabled ? {
          type: 'ghost',
          disabled: true
        } : undefined}
      >
        <NiForm
          titleline
          style={{ width: '100%' }}
          direction="vertical"
          items={[{
            label: localizedString('common_attribute'),
            key: 'variable',
            labelExtra: <NiCommonCascader
              value={state.data && state.data.variable}
              disabled={props.disabled}
              style={{ width: 160, alignSelf: 'flex-end' }}
              placeholder={localizedString('condition_add')}
              key={SELECTION_KEY.AVAILABLE_CONDITION}
              selectionkey={SELECTION_KEY.AVAILABLE_CONDITION}
              rootpath={[{ value: props.frameId, value_type: props.frameType }]}
              onChange={this.handleSelectParameter}
            // disabledvalues={state.disabledValues}
            />
          },
          {
            key: 'parameter',
            label: localizedString('input_parameters'),
            content: <InputParameterComp
              style={{ flex: 1 }}
              function={state.data && state.data.variable || []}
              frame={{ id: props.frameId, type: props.frameType as FrameType }}
              disabled={props.disabled}
              data={state.data && state.data.parameters || []}
              slots={props.parameters}
              onChange={this.handleParametersChange}
            />
          },
          {
            key: 'conditions',
            label: localizedString('common_condition'),
            extra: <AddButton
              disabled={state.isTable || props.disabled}
              onClick={this.handleNewCondition}
            />,
            content: <div className="trigger-table-wrap">
              <NiTable
                rowKey={(record: ConditionContent) => state.data &&
                  `${state.data.conditions.findIndex((item: ConditionContent) =>
                    item === record)}` || ''}
                dataSource={state.data && state.data.conditions}
                columns={this.getColumns()}
                pagination={false}
              />
            </div>
          }]}
        />
      </NiModal>
    );
  }

  handleSelectParameter = (value: string[], selectedOptions?: SelectionOption[]) => {

    const state = this.state;
    const variable: ReferenceNode[] = [];
    const conditions: ConditionContent[] = [];
    let isTable = false;

    (selectedOptions || []).forEach((opt: SelectionOption) => {
      if (opt.valueType === SpecialValueType.Size) {
        conditions.push({
          kind: 'content',
          operationAt: 'table_row',
          operation: Operation.GreaterThanCondition,
          value: ['']
        });
        isTable = true;
      } else if (opt.valueType === SpecialValueType.Index) {
        const v = variable[variable.length - 1];
        v.showIndex = true;
        v.index = opt.index;
      } else {
        variable.push({
          id: opt.value as string,
          name: opt.label as string || '',
          type: opt.valueType as ParameterType
        });
      }
    });

    if (!isTable &&
      !isEqual(state.data && state.data.conditions, [{
        kind: 'content',
        operationAt: 'table_row',
        operation: Operation.GreaterThanCondition,
        value: ['']
      }]) &&
      state.data &&
      isEqual(variable, state.data.variable)) {
      return;
    }

    let newCondition: TriggerCondition = {
      variable,
      conditions,
      parameters: [],
      kind: 'condition'
    };

    newCondition = this.initData(newCondition);

    const node = newCondition.variable[newCondition.variable.length - 1];
    const param = ParameterMapStore.get(node.id);
    const isMultiValue = !node.showIndex && param && param.multiValue || false;
    const isBoolean = param
      && param.proto
      && isSystemBoolean(param.proto) || false;
    const isFrameSlot = param
      && param.proto
      && param.proto.type === ParameterType.COMPOSITE || false;

    this.setState({
      isTable,
      isMultiValue,
      isBoolean,
      isFrameSlot,
      data: newCondition
    });
  }

  handleParametersChange = (next: InputParameter[]) => {

    const data = cloneDeep(this.state.data);

    if (!data) {
      return;
    }
    data.parameters = next;

    this.setState({
      data
    });
  }

  onInit = async () => {
    const disabledValues: string[][] = [];

    this.props.triggerConditions.forEach((c: TriggerCondition) => {
      const values: string[] = [];

      (c.variable || []).forEach((n: ReferenceNode) => {
        if (n.showIndex) {
          values.push('index');
        } else {
          values.push(n.id);
        }
      });

      if (c.conditions && c.conditions.length && c.conditions[0].operationAt === 'table_row') {
        values.push('size');
      }
      disabledValues.push(values);
    });

    this.setState({
      disabledValues,
      data: cloneDeep(this.props.data)
    }, this.updateStatus);
  };

  updateStatus = () => {

    const target = cloneDeep(this.state.data);
    if (!target) {
      return;
    }

    const isTable = target &&
      target.conditions &&
      target.conditions.length &&
      target.conditions[0].operationAt === 'table_row' || false;

    const node = target && target.variable[target.variable.length - 1];
    const param = node && ParameterMapStore.get(node.id);
    const isMultiValue = node && param && !node.showIndex && param.multiValue || false;
    const isBoolean = param
      && param.proto
      && isSystemBoolean(param.proto) || false;

    this.setState({
      isTable,
      isMultiValue,
      isBoolean,
      data: target
    });
  }


  dirtyCheck = () => {

    const props = this.props;
    const state = this.state;

    if (!props.data && state.data) {
      return true;
    }

    return !isEqual(this.state.data, this.props.data);
  }

  onCancel = () => {
    const props = this.props;

    if (this.dirtyCheck()) {
      discardModal(() => {
        this.setState({
          data: undefined
        });
        props.close();
      });
      return;
    }

    this.setState({
      data: undefined
    });
    props.close();
  };

  onConfirm = debounce(() => {
    const state = this.state;
    const props = this.props;

    if (props.disabled) {
      return;
    }

    if (!state.data) {
      return;
    }

    const haveAny = !!(state.data.conditions || []).find(
      (item: ConditionContent) =>
        !state.isTable &&
        (item.operation === Operation.RequireCondition || item.operation === Operation.NotEmptySetCondition)
    );
    const haveUnKnowm = !!(state.data.conditions || []).find(
      (item: ConditionContent) =>
        !state.isTable &&
        item.operation === Operation.UnKonwnCondition
    );

    const notHaveRowAndColConditions = (state.data.conditions || []).filter(
      (item: ConditionContent) =>
        !state.isTable
    );

    if (
      (haveAny || haveUnKnowm) &&
      notHaveRowAndColConditions.length > 1
    ) {
      const warning = () => {
        message.warning(localizedString('condition_confirm_warning', {
          operation: haveAny
            ? localizedString('operation_require')
            : localizedString('operation_unknown')
        }));

      };
      warning();

      return;
    }

    props.confirm(state.data);
  }, 500);

  getColumns = () => {
    const state = this.state;
    const props = this.props;

    const columns: any = [
      {
        title: localizedString('condition_operation'),
        className: 'trigger-table__operation',
        dataIndex: 'operation',
        width: '45%',
        render: (operation: number, record: ConditionContent, index: number) => {
          if (!state.data) {
            return [];
          }

          return <OperationColumnComp
            disabled={props.disabled}
            index={index}
            data={state.data}
            isBoolean={state.isBoolean}
            isTable={state.isTable}
            isMultiValue={state.isMultiValue}
            record={record}
            handleOperationChange={this.handleDataChange}
          />;
        }
      },
      {
        title: localizedString('condition_value'),
        className: 'trigger-table__value',
        key: 'value',
        dataIndex: 'value',
        width: '45%',
        shouldCellUpdate: (record: ConditionContent, preRecord: ConditionContent) => {
          return !isEqual(record, preRecord);
        },
        render: this.getValueComp
      }
    ];

    if (state.isTable) {
      columns.unshift({
        title: localizedString('attribute_name'),
        className: 'trigger-table__index',
        key: 'slot-name',
        width: 150,
        render: this.isSizeComp
      });
    }

    return columns;
  };

  isSizeComp = (name: string, record: TriggerCondition | ConditionContent, index: number) => {
    return this.state.data && `${this.state.data.variable.map((node: ReferenceNode) =>
      node.name).join('.')}.size`;
  };

  getValueComp = (value: any, record: ConditionContent, index: number) => {
    const { isTable, isBoolean, isMultiValue, data } = this.state;
    const props = this.props;

    return (
      <div
        key="value-comp"
        style={{
          position: 'relative',
          padding: '0 20px 0 0',
          minHeight: 32
        }}
      >
        {(record.operation === Operation.UnKonwnCondition && (
          <div className="any-value">{localizedString('operation_unknown')}</div>
        )) ||
          ((record.operation === Operation.RequireCondition ||
            record.operation === Operation.NotEmptySetCondition) && (
              <div className="any-value">{localizedString('operation_any')}</div>
            )) ||
          (
            <ValueSwitch
              key={`value_${index}`}
              disabled={props.disabled}
              variable={data && data.variable || []}
              isBoolean={isBoolean}
              isTable={isTable}
              isMultiValue={isMultiValue}
              parameters={props.parameters}
              data={record}
              onChange={(next: any[]) => {

                if (!data || !data.conditions) {
                  return;
                }
                const nextData = cloneDeep(data);
                const curCondition = nextData.conditions[index];

                curCondition.value = next;

                this.setState({
                  data: nextData
                });
              }}
            />
          )}

        {data && data.conditions.length > 1 && (
          <DeleteOutlined
            key="delete-condition"
            style={{
              position: 'absolute',
              right: 0,
              top: 8
            }}
            className="delete-btn"
            onClick={() => {
              if (!data) {
                return;
              }

              const next = cloneDeep(data);

              next.conditions.splice(index, 1);
              this.setState({
                data: next
              });
            }} />
        )}
      </div>
    );
  };

  handleNewCondition = (e: any) => {
    const state = this.state;
    const props = this.props;

    if (!state.data) {
      return;
    }

    this.setState({
      data: {
        ...state.data,
        conditions: [
          ...state.data.conditions,
          {
            kind: 'content',
            operation: state.isMultiValue
              ? Operation.NotEmptySetCondition
              : Operation.RequireCondition,
            value: []
          }
        ]
      }
    });
  };

  initData = (data: TriggerCondition) => {
    const target = cloneDeep(data);
    const node = target && target.variable[target.variable.length - 1];
    const param = ParameterMapStore.get(node.id);
    const isMultiValue = !node.showIndex && param && param.multiValue || false;
    if (!target.conditions || !target.conditions.length) {
      target.conditions = [{
        kind: 'content',
        operation: isMultiValue
          ? Operation.NotEmptySetCondition
          : Operation.RequireCondition,
        value: []
      }];
    }
    return target;
  }

  handleDataChange = (next: TriggerCondition) => {
    this.setState({
      data: next
    });
  };
}
